import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { invalidateManagerQueues } from './invalidateManagerQueues';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth';
import { onColdStart } from '@/lib/realtimeColdStart';

/**
 * Phase 8 (2026-05-07) — Manager Realtime.
 *
 * Subscribes to `three_cs` INSERT + UPDATE events filtered by
 * `status=eq.pushed` and invalidates the manager queue queries on any
 * qualifying event, so counts and lists stay current without a refresh.
 *
 * Supabase Realtime already enforces RLS — clients only receive events
 * for rows they could SELECT. The existing `three_cs` SELECT policy
 * gates by `user_can_access_location(trucks.location_id)`, so a
 * Jenison manager only receives events for Jenison trucks. Cross-
 * location and cross-company isolation is automatic.
 *
 * SILENT on purpose (2026-10-07). This used to pop a "New 3C in your queue"
 * toast on every push. Shane asked for the per-line alerts to go — "we need to
 * be looking at work orders as a whole" — so the manager is now told once per
 * work order, by the `work_order_closed` bell, when the technician marks it
 * done. The refresh stays: a quiet cache update is not an alert.
 *
 * Channel name: `manager-readyToEnter:${user.companyId}` — per-company
 * scoping keeps subscriptions clean for the multi-tenant case.
 */

type ManagerRole = 'manager' | 'admin' | 'leadership';

function isManagerRole(role: string | null | undefined): role is ManagerRole {
  return role === 'manager' || role === 'admin' || role === 'leadership';
}

export function useManagerRealtime(): void {
  const { user } = useAuth();
  const utils = trpc.useUtils();

  const role = user?.role;
  const companyId = user?.companyId;

  useEffect(() => {
    if (!companyId) return;
    if (!isManagerRole(role)) return;

    const channelName = `manager-readyToEnter:${companyId}`;

    const handlePushedEvent = () => {
      invalidateManagerQueues(utils);
    };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'three_cs',
          filter: 'status=eq.pushed',
        },
        handlePushedEvent,
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'three_cs',
          filter: 'status=eq.pushed',
        },
        handlePushedEvent,
      )
      .subscribe(
        // Cold-start refetch (Bug K defense, 2026-05-08): close the
        // gap between mount and SUBSCRIBED so a 3C pushed in that
        // window doesn't sit invisible until the next manual refresh.
        //
        // At scale (10+ concurrent managers), consider consolidating
        // dashboardStats into a single subscription site rather than
        // refetching on every manager-surface mount. Sprint B+ scope.
        onColdStart(() => {
          invalidateManagerQueues(utils);
        }),
      );

    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, companyId]);
}
